import { GitCompare } from 'lucide-react';
import { useRouter } from 'next/router';
import { useQueryState } from 'nuqs';
import { type ReactElement, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { AppLayout } from '@/components/layouts/app-layout';
import { SearchLayout } from '@/components/views/search/routes/layout';
import { usePlaceCache } from '@/hooks/use-place-cache';
import { useDisruptionAlerts } from '@/hooks/use-platform-alerts';
import { useRouteSearch } from '@/hooks/use-route-search';
import { useSavedItems } from '@/hooks/use-saved-items';
import { useWatchQueryParams } from '@/hooks/use-watch-query-params';
import { DEFAULT_NUQS_CONFIG, SAVED_ITEM_TYPE, SITE_NAME } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { routeSearchDestinationState, routeSearchOriginState } from '@/state/routes';
import {
  cargoSearchParamsState,
  DEFAULT_DEVELOPER_SEARCH_PARAMS,
  DEFAULT_SEARCH_PARAMS,
  developerSearchParamsState,
  newSearchIsNeededState,
  resetAdvancedSearchParamsState,
  searchParamsSelectorState,
} from '@/state/search';
import { DeveloperSearchQueryInterface, SearchQueryInterface } from '@/types';
import { PlaceInterface } from '@/types/api-types';
import { initialiseUserSearchCount } from '@/utils/helpers';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { parseAsAdvancedSearch } from '@/utils/nuqs/parse-as-advanced-search';
import { parseAsCargoOption } from '@/utils/nuqs/parse-as-cargo-option';
import { searchStateToQueryParams } from '@/utils/route';

const Page = (): ReactElement => {
  const router = useRouter();
  const { createSavedItem } = useSavedItems();
  const setIsNewSearchNeeded = useSetRecoilState(newSearchIsNeededState);
  const { getPlaceWithLocation } = usePlaceCache();
  const resetAdvancedSearchParams = useResetRecoilState(resetAdvancedSearchParamsState);
  const selectedDeveloperSearchParams = useRecoilValue(developerSearchParamsState);
  const [selectedSearchParams, setSelectedSearchParams] = useRecoilState(searchParamsSelectorState);
  const [cargoParams, setCargoParams] = useRecoilState(cargoSearchParamsState);
  const [selectedOrigin, setSelectedOrigin] = useRecoilState(routeSearchOriginState);
  const [selectedDestination, setSelectedDestination] = useRecoilState(routeSearchDestinationState);
  const [origin, setOrigin] = useState<PlaceInterface>();
  const [destination, setDestination] = useState<PlaceInterface>();
  const [advancedSearchParams, setAdvancedSearchParams] = useState<
    SearchQueryInterface & DeveloperSearchQueryInterface
  >();
  const [developerSearchParams, setDeveloperSearchParams] = useState<DeveloperSearchQueryInterface>(
    DEFAULT_DEVELOPER_SEARCH_PARAMS,
  );

  const [advancedSearchUrlParams] = useQueryState(
    QUERY_PARAM.ADVANCED_SEARCH,
    parseAsAdvancedSearch.withOptions(DEFAULT_NUQS_CONFIG).withDefault(DEFAULT_SEARCH_PARAMS),
  );

  const [cargoSearchUrlParams] = useQueryState(QUERY_PARAM.CARGO, parseAsCargoOption.withOptions(DEFAULT_NUQS_CONFIG));

  const { forceSearch } = useRouteSearch({
    origin,
    destination,
    advancedSearchParams,
    cargoParams: cargoSearchUrlParams || undefined,
  });

  const handleParamsUpdate = async ({
    origin: newOrigin,
    destination: newDestination,
    advancedSearch: newAdvancedSearchParams,
    cargo: newCargoOptions,
  }: {
    origin?: PlaceInterface;
    destination?: PlaceInterface;
    advancedSearch?: string;
    cargo?: string;
  }) => {
    const originWithLocation = await getPlaceWithLocation({ place: newOrigin });
    const destinationWithLocation = await getPlaceWithLocation({ place: newDestination });

    setOrigin(originWithLocation);
    setDestination(destinationWithLocation);
    setSelectedOrigin(originWithLocation);
    setSelectedDestination(destinationWithLocation);

    if (newAdvancedSearchParams) {
      try {
        const parsedAdvancedSearchParams = JSON.parse(newAdvancedSearchParams);
        setSelectedSearchParams(parsedAdvancedSearchParams);
        setAdvancedSearchParams({
          ...parsedAdvancedSearchParams,
          ...developerSearchParams,
        });
      } catch (e) {
        console.log('Error parsing advanced search params', e);
      }
    } else {
      resetAdvancedSearchParams();
      setAdvancedSearchParams(developerSearchParams);
    }

    if (newCargoOptions) {
      try {
        const parsedCargoSearchParams = JSON.parse(newCargoOptions);
        setCargoParams(parsedCargoSearchParams);
      } catch (e) {
        console.log('Error parsing cargo search params', e);
      }
    }
  };

  useWatchQueryParams({
    paramsToWatch: [QUERY_PARAM.ORIGIN, QUERY_PARAM.DESTINATION, QUERY_PARAM.ADVANCED_SEARCH, QUERY_PARAM.CARGO],
    onParamsDidUpdate: handleParamsUpdate,
  });

  initialiseUserSearchCount();
  // Latest Platform Alerts
  useDisruptionAlerts();

  const handleSearch = () => {
    const query = searchStateToQueryParams({
      origin: selectedOrigin,
      destination: selectedDestination,
      advancedSearchParams: selectedSearchParams,
      cargoParams,
    });

    // We don't add developer search params to the URL, so just set them directly
    setDeveloperSearchParams(selectedDeveloperSearchParams);

    router.push({ query }, undefined, {
      shallow: true,
    });
  };

  const handleCreateSavedItem = async () => {
    const newSavedItem: SavedItemModel = {
      data: JSON.stringify({
        origin: selectedOrigin,
        destination: selectedDestination,
        advancedSearchParams: selectedSearchParams,
        cargoParams,
      }),
      label: `${selectedOrigin?.shortName} to ${selectedDestination?.shortName}`,
      type: SAVED_ITEM_TYPE.ROUTE,
    };
    await createSavedItem(newSavedItem);
  };

  useEffect(() => {
    const currentUrl = searchStateToQueryParams({
      origin,
      destination,
      advancedSearchParams: advancedSearchUrlParams,
      cargoParams: cargoSearchUrlParams || {},
    });

    const newUrl = searchStateToQueryParams({
      origin: selectedOrigin,
      destination: selectedDestination,
      advancedSearchParams: selectedSearchParams,
      cargoParams,
    });

    const isUrlGoingToChange = JSON.stringify(currentUrl) !== JSON.stringify(newUrl);

    setIsNewSearchNeeded(isUrlGoingToChange);
  }, [selectedOrigin, selectedDestination, origin, destination, selectedSearchParams, cargoParams]);

  return (
    <AppLayout
      title={`Routing - ${SITE_NAME}`}
      breadcrumbItems={[
        { label: 'Routing' },
        { label: 'New search', icon: <GitCompare className="text-lightBlue-500" /> },
      ]}
    >
      <SearchLayout onSearch={handleSearch} onCreateSavedItem={handleCreateSavedItem} onForceSearch={forceSearch} />
    </AppLayout>
  );
};

export default Page;
